var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import Big from 'big.js';
import { LRUCache } from 'lru-cache';
import * as murmur from 'murmurhash';
import { Either, EitherAsync, Left, Right } from 'purify-ts';
import * as api from '@codegrade/api';
import { Fraction } from '@codegrade/bigfraction';
import { Maybe, Nothing } from '@codegrade/maybe';
import { assert, datetime, decode, expr, maybe, object, sort } from '@codegrade/utils';
import client from '@/client';
import * as lti from '@/lti_providers';
import { Directory } from '@/models/fileTree';
import { CoursePermissions as CPerm } from '@/permissions';
import { CodeStore, CoursesStore, SiteSettingsStore, UserStore } from '@/store';
import * as utils from '@/utils';
import { not } from '@/utils';
import { CacheDecorator } from '@/utils/cache';
import { Course } from './course';
import { Glob } from './glob';
/** The default max grade. */
export const DEFAULT_MAX_GRADE = 10;
/**
 * Check that two availability values are equal.
 *
 * @param self The first value to compare.
 * @param other The other value to compare.
 * @returns If the two given values should be considered equal.
 */
export function availabilityEquals(self, other) {
    return expr.match(self, 'tag', {
        fixed: ({ value: selfValue }) => other.tag === 'fixed' && selfValue === other.value,
        timed: ({ availableAt: selfAvailableAt }) => other.tag === 'timed' && selfAvailableAt.isSame(other.availableAt),
    });
}
/**
 * Check that two grade availability values are equal.
 *
 * @param self The first value to compare.
 * @param other The other value to compare.
 * @returns If the two given values should be considered equal.
 */
export function gradeAvailabilityEquals(self, other) {
    return expr.match(self, 'tag', {
        fixed: ({ value }) => other.tag === 'fixed' && other.value === value,
    });
}
/**
 * Check that grading values are equal.
 *
 * @param self The first value to compare.
 * @param other The other value to compare.
 * @returns If the two given values should be considered equal.
 */
export function gradingEquals(self, other) {
    return expr.match(self, 'scale', {
        points: ({ points }) => other.scale === 'points' && other.points.equals(points),
        percentage: () => other.scale === 'percentage',
    });
}
/**
 * Check whether an assignment is available for the current user.
 *
 * @param assignment The assignment to check.
 * @param getNow A function that should return the current time, used when the availability tag
 * is "timed".
 * @returns If the assignment is available right now.
 */
export function isAssignmentAvailable(assignment, getNow) {
    return expr.match(assignment.timeframe.availability, 'tag', {
        fixed: ({ value }) => value,
        timed: ({ availableAt }) => availableAt.isSameOrBefore(getNow(availableAt)),
    });
}
const anonymizeWithMurmurV3 = (() => {
    const cache = new LRUCache({ max: 3000 });
    return (key) => {
        let value = cache.get(key);
        if (value == null) {
            value = `Anonymous ${murmur.v3(key).toString(10)}`;
            cache.set(key, value);
        }
        return value;
    };
})();
// eslint-disable-next-line
export class Assignment extends api.ExtendedAssignment {
    /** The private cache of this class. */
    #cache = new CacheDecorator();
    /** The public cache of the class, only be used in the class itself. */
    get _cache() {
        return this.#cache;
    }
    /**
     * @param props The properties of the new assignment.
     */
    constructor(props) {
        super(props);
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get canManage() {
        return this.course.mapOrDefault(c => c.canManageAssignment, false);
    }
    /** @returns The glob for the file to load first. */
    get fileToLoadFirstGlob() {
        return this.fileToLoadFirst
            .chain(f => {
            try {
                return Maybe.of(Glob.make(f));
            }
            catch {
                return Nothing;
            }
        })
            .orDefaultLazy(() => Glob.default());
    }
    /**
     * Is this assignment available.
     *
     * @param getNow A function that should return the current time, used when the availability tag
     * is "timed".
     * @returns If the assignment is available right now.
     */
    getIsAvailable(getNow) {
        return isAssignmentAvailable(this, getNow);
    }
    /**
     * @returns The course of this assignment, from the store.
     */
    get apiCourse() {
        return CoursesStore.getCourse()(this.courseId);
    }
    /**
     * @returns The course of this assignment, from the store.
     */
    get course() {
        return this.apiCourse.filter(c => c instanceof Course);
    }
    /** @returns The id of the tenant that this assignment is connected to. */
    get tenantId() {
        return this.apiCourse.map(course => course.tenantId);
    }
    /** @returns Wether the webhook upload type is enabled on this assignment. */
    get webhookEnabled() {
        return this.webhookConfiguration.tag === 'enabled';
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get ltiProvider() {
        if (!this.isLti) {
            return Nothing;
        }
        return this.course.chain(c => c.ltiProvider);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getReminderTimeOrDefault(now) {
        if (this.reminderTime.isJust()) {
            return this.reminderTime.value.clone();
        }
        const baseTime = this.timeframe.deadline.caseOf({
            Just: deadline => {
                if (deadline.isBefore(now)) {
                    return now;
                }
                return deadline;
            },
            Nothing: () => now,
        });
        return baseTime.clone().add(1, 'weeks');
    }
    /* eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation. */
    get created_at() {
        return datetime.formatDate(this.createdAt);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getDeadlineAsString() {
        return this.timeframe.deadline
            .map(deadline => datetime.formatDate(deadline))
            .extractNullable();
    }
    // eslint-disable-next-line
    getFormattedDeadline() {
        return this.timeframe.deadline
            .map(deadline => datetime.readableFormatDate(deadline))
            .extractNullable();
    }
    /**
     * The date the assignment becomes available in a readable format.
     *
     * @returns The date the assignment will become available at in a readable format. Will return
     * `Nothing` when the assignment doesn't have an available at set.
     */
    getFormattedAvailableAt() {
        return expr.match(this.timeframe.availability, 'tag', {
            fixed: () => Maybe.empty(),
            timed: ({ availableAt }) => Maybe.of(datetime.readableFormatDate(availableAt)),
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getFormattedCreatedAt() {
        return datetime.readableFormatDate(this.createdAt);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    deadlinePassed(now) {
        return this.timeframe.deadline.mapOrDefault(deadline => now.isAfter(deadline), false);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    lockDatePassed(now) {
        return this.timeframe.lockDate.mapOrDefault(lockDate => lockDate.isBefore(now), false);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get isExam() {
        return this.kind === 'exam';
    }
    /**
     * Is this an exam that hasn't started yet.
     *
     * @param now The current time to determine if this exam hasn't started yet.
     * @returns True if this is an exam, and it hasn't started yet.
     */
    isNotStartedExam(now) {
        return expr.match(this.timeframe.availability, 'tag', {
            fixed: () => false,
            timed: ({ availableAt }) => this.isExam && availableAt.isAfter(now),
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get peerFeedbackDeadline() {
        return maybe
            .and(this.timeframe.deadline, this.peerFeedbackSettings.chain(object.attr('time')))
            .map(([deadline, time]) => deadline.clone().add(time, 'seconds'))
            .extractNullable();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    peerFeedbackDeadlinePassed(now, dflt = true) {
        const deadline = this.peerFeedbackDeadline;
        if (deadline == null) {
            return dflt;
        }
        return now.isAfter(deadline);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getSubmitDisabledReasons(now) {
        if (this.course.mapOrDefault(c => !c.hasBeenPaid, false)) {
            return ['you have not paid for this course'];
        }
        const submitOwn = this.hasPermission(CPerm.can_submit_own_work);
        const submitOther = this.hasPermission(CPerm.can_submit_others_work) ||
            this.hasPermission(CPerm.can_submit_others_in_section_work);
        const res = [];
        if (!(submitOwn || submitOther)) {
            res.push('you do not have the permission to submit work for this course');
        }
        if (!this.hasPermission(CPerm.can_upload_after_deadline) && this.deadlinePassed(now)) {
            res.push("the assignment's deadline has already passed");
        }
        if (!this.hasPermission(CPerm.can_upload_after_lock_date) && this.lockDatePassed(now)) {
            res.push('the assignment is locked');
        }
        return res;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    canSubmitWork(now) {
        return this.getSubmitDisabledReasons(now).length === 0;
    }
    /** @returns The number of decimal places to display for grades, based on the grading scale and tenant settings. */
    get gradeDecimalAmount() {
        const settingName = (() => {
            switch (this.grading.scale) {
                case 'points':
                    return 'ASSIGNMENT_POINT_DECIMALS';
                case 'percentage':
                    return 'ASSIGNMENT_PERCENTAGE_DECIMALS';
                default:
                    return assert.AssertionError.assertNever(this.grading);
            }
        })();
        return this.course.caseOf({
            Just: course => SiteSettingsStore.getSetting()(settingName, course.tenantId),
            Nothing: () => SiteSettingsStore.getGlobalSetting()(settingName),
        });
    }
    /**
     * The multiplier to determine the grade from our 10-points internal scale.
     *
     * Depends on the grading scale of the assignment only.
     */
    get gradeMultiplier() {
        if (this.grading.scale === 'points') {
            return this.grading.points.div(10);
        }
        return new Fraction(10);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    formatUnscaledGrade(grade) {
        let bigGrade;
        if (typeof grade === 'number') {
            bigGrade = new Big(grade);
        }
        else if (grade instanceof Fraction) {
            bigGrade = new Big(this.toScaledGrade(grade).valueOf());
        }
        else {
            bigGrade = grade;
        }
        const scaledString = this.toScaledGrade(bigGrade).toFixed(this.gradeDecimalAmount);
        switch (this.grading.scale) {
            case 'points':
                return scaledString;
            case 'percentage':
                return `${scaledString}%`;
            default:
                return assert.AssertionError.assertNever(this.grading);
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    formatScaledGrade(grade) {
        return this.formatUnscaledGrade(this.fromScaledGrade(grade));
    }
    /**
     * The scaled grade, taking the grade scale points into consideration.
     *
     * @param grade The grade to scale.
     * @returns The scaled grade.
     */
    toScaledGrade(grade) {
        const mult = this.gradeMultiplier.valueOf();
        if (typeof grade === 'number') {
            return grade * mult;
        }
        else if (grade instanceof Fraction) {
            return grade.mul(this.gradeMultiplier);
        }
        else {
            return grade.mul(mult);
        }
    }
    /**
     * The unscaled grade, not taking the max grade and scale into consideration.
     *
     * ATTENTION! If the scale is set to 0 points, the return will always be zero.
     *
     * @param grade The grade to unscale.
     * @returns The unscaled grade.
     */
    fromScaledGrade(grade) {
        const mult = this.gradeMultiplier.valueOf();
        if (mult === 0) {
            return 0;
        }
        else if (typeof grade === 'number') {
            return grade / mult.valueOf();
        }
        else if (grade instanceof Fraction) {
            return grade.div(this.gradeMultiplier);
        }
        else {
            return grade.div(mult.valueOf());
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get scaledMaxGrade() {
        return this.toScaledGrade(this.maxGrade);
    }
    /**
     * Check if the user has the given permission in the course.
     *
     * @param permission The permission to check.
     * @returns If the user has this permission.
     * @deprecated Pass the course seperatly instead.
     */
    hasPermission(permission) {
        return this.course.mapOrDefault(c => c.hasPermission(permission), false);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _canSeeFeedbackType(typ) {
        if (this.gradeAvailability.value) {
            return true;
        }
        const perm = {
            grade: CPerm.can_see_grade_before_open,
            user: CPerm.can_see_user_feedback_before_done,
            peer: CPerm.can_see_peer_feedback_before_done,
        }[typ];
        if (perm == null) {
            throw new Error(`Requested feedback type "${typ}" is not known.`);
        }
        return this.hasPermission(perm);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    canSeeGrade() {
        return this._canSeeFeedbackType('grade');
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    canEditGrade() {
        return this.hasPermission(CPerm.can_grade_work);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    canSeeAllInlineFeedback() {
        const res = this._canSeeFeedbackType('user');
        return this.peerFeedbackSettings.caseOf({
            Just: () => res && this._canSeeFeedbackType('peer'),
            Nothing: () => res,
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get canBeCopiedInto() {
        return [
            CPerm.can_edit_cgignore,
            CPerm.can_edit_assignment_info,
            CPerm.manage_rubrics,
            CPerm.can_edit_autotest,
            CPerm.can_edit_group_set,
        ].every(perm => this.hasPermission(perm));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    loadDescription() {
        if (!this.hasDescription) {
            return EitherAsync.liftEither(Either.of(''));
        }
        return client.assignment
            .getDescription({ assignmentId: this.id })
            .chain(async ({ cgResult }) => decode.bufferE(new Uint8Array(cgResult)));
    }
    /** @returns The anonymization method to use, if any. */
    get _anonymizationMethod() {
        return this.anonymizedGrading.map(algorithm => expr.switcher(algorithm, {
            murmur_v3: () => 
            /**
             * The actual anonymization method to use for the assignment.
             *
             * @param id The id to hash.
             * @returns An anonymized name.
             */
            (id) => anonymizeWithMurmurV3(`${id} ${this.id}`),
        }));
    }
    /** @returns Maybe the anonymization method to use for the current user. */
    get anonymizationMethod() {
        return this._anonymizationMethod.filter(() => !this.hasPermission(CPerm.can_see_anonymized_names));
    }
    /**
     * Get the name of the given section as it should be displayed in this assignment.
     *
     * @param user The user for which the section is displayed. If this is the same as the current
     * user the actual section name is used.
     * @param section The section that you want to display.
     * @returns The (maybe anonymized) name of the given section.
     */
    getSectionName(user, section) {
        const loggedInUser = UserStore.loggedInUser();
        return this.anonymizationMethod
            .filter(() => !loggedInUser.mapOrDefault(u => utils.user.isEqualOrMemberOf(user, u), false))
            .caseOf({
            Just: method => method(section.id),
            Nothing: () => section.name,
        });
    }
    /**
     * Sort the given assignments.
     *
     * They are sorted based on their name (case insensitively), if the name is the same the id is
     * used to always provide a stable sort.
     *
     * @param assignments The assignments you want to sort.
     * @returns A fresh array that contains the given assignments, but sorted.
     */
    static sortAssignments(assignments) {
        return sort.sortBy(assignments, assig => [assig.name, assig.id], {
            caseInsensitive: true,
        });
    }
    /** @returns The file tree of the editor template of this assignment. */
    loadEditorTemplate() {
        return client.assignment.getTemplate({ assignmentId: this.id }).chain(response => EitherAsync.liftEither(response.mapEither(({ files }) => files.caseOf({
            Nothing: () => Right(Maybe.empty()),
            Just: f => {
                if (f instanceof api.DirectoryWithChildren) {
                    return Right(Maybe.of(Directory.fromServerData(f, null)));
                }
                else {
                    return Left(Error('Got invalid data.'));
                }
            },
        }))));
    }
    /**
     * Get the contents of a single editor template file.
     *
     * @param file The metadata of the file to load.
     * @returns The requested file data, if it exists, or an error.
     */
    loadEditorTemplateFile(file) {
        return EitherAsync.fromPromise(() => CodeStore.loadCodeFromRoute({
            route: [
                'api',
                'v1',
                'assignments',
                this.id.toString(10),
                'template',
                file.id.toString(10),
            ],
            hash: file.hash,
        }).then(utils.responseWithValue, Left));
    }
    /**
     * Format the grade of the submission to a string.
     *
     * @param sub The submission to format the grade of.
     * @returns The formatted grade.
     */
    formatGrade(sub) {
        return sub.grade.map(grade => this.formatUnscaledGrade(grade));
    }
}
__decorate([
    CacheDecorator.cache
], Assignment.prototype, "fileToLoadFirstGlob", null);
__decorate([
    CacheDecorator.cache
], Assignment.prototype, "_anonymizationMethod", null);
/** Class used to retrieve the permissions and capabilities for each assignment. */
export class AssignmentCapabilities {
    /** The assignment, if it was loaded when trying to determine its capabilities. */
    assignment;
    /** @param assignment The assignment retrieved from the store. */
    constructor(assignment) {
        this.assignment = Maybe.fromNullable(assignment);
    }
    /**
     * Returns the chosen property from the lti provider connected to this assignment
     * if the assignment and the provider are available.
     *
     * @param feature The feature to retrieve from the connected lti provider.
     * @returns The chosen LTI provider property if a provider is available.
     */
    getLtiProp(feature) {
        return this.ltiProvider.map(prov => prov[feature]);
    }
    /** @returns The lti provider connected to the assignment. */
    get ltiProvider() {
        return this.assignment.chain(assig => assig.ltiProvider).map(lti.makeProvider);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get canEditState() {
        return this.canEditInfo;
    }
    /** @returns Whether the User has permission to edit the information of the assignment. */
    get canEditInfo() {
        return this.assignment.mapOrDefault(a => a.hasPermission(CPerm.can_edit_assignment_info), false);
    }
    /** @returns Whether the User has permission to edit the name of the assignment. */
    get canEditName() {
        return this.canEditInfo && this.getLtiProp('supportsName').mapOrDefault(not, true);
    }
    /** @returns Whether the User has permission to edit the deadline of the assignment. */
    get canEditDeadline() {
        return this.canEditInfo && this.getLtiProp('supportsDeadline').mapOrDefault(not, true);
    }
    /** @returns Whether the User has permission to edit the lockdate of the assignment. */
    get canEditLockDate() {
        if (!this.canEditInfo) {
            return false;
        }
        return this.ltiProvider.caseOf({
            Just: prov => {
                if (!prov.supportsLockDate) {
                    return true;
                }
                return this.assignment
                    .chain(assig => assig.course)
                    .mapOrDefault(course => !SiteSettingsStore.getSetting()('LTI_LOCK_DATE_COPYING_ENABLED', course.tenantId), !SiteSettingsStore.getGlobalSetting()('LTI_LOCK_DATE_COPYING_ENABLED'));
            },
            Nothing: () => true,
        });
    }
    /**
     * Returns whether the user is granted a determined permission on this assignment if the
     * assignment was loaded. Defaults to false otherwise.
     *
     * @param permission The permission to look up.
     * @returns The result from the permission lookup if the assignment is loaded, false otherwise.
     */
    hasPermission(permission) {
        return this.assignment.mapOrDefault(a => a.hasPermission(permission), false);
    }
    /** @returns Whether the User has permission to edit the availability of the assignment. */
    get canEditAvailableAt() {
        return (this.canEditInfo &&
            this.getLtiProp('supportsAvailableAtManagement').mapOrDefault(not, true));
    }
    /** @returns Whether the User has permission to edit the max grade of the assignment. */
    get canEditMaxGrade() {
        return (this.hasPermission(CPerm.can_edit_maximum_grade) &&
            this.getLtiProp('supportsBonusPoints').orDefault(true));
    }
    /** @returns Whether the current user has permission to see all this assignment's timeframes. */
    get canViewTimeframes() {
        return this.hasPermission(CPerm.can_view_all_assignment_timeframes);
    }
    /** @returns Whether the User has permission to edit the submission settings of the assignment. */
    get canEditSubmissionSettings() {
        return this.canEditInfo;
    }
    /** @returns Whether the User has permission to update their grader status in assignment. */
    get canUpdateGraderStatus() {
        return this.hasPermission(CPerm.can_grade_work) || this.canUpdateOtherGraderStatus;
    }
    /** @returns Whether the User has permission to update other graders' status in the assignment. */
    get canUpdateOtherGraderStatus() {
        return this.hasPermission(CPerm.can_update_grader_status);
    }
}
